import {
  useRideQualityRoughness,
} from '@src/modules/performance/context/reports-api-hooks';
import { selectedDatabaseIdState, currentDatabaseSelector } from '@transport-insights/uikit';
import { useRecoilValue } from 'recoil';
import GroupedRegionMessage from '@src/modules/performance/components/GroupedRegionMessage';
import { Link, Text } from '@chakra-ui/react';
import LineRangeAreaChart from '@src/modules/performance/components/charts/LineRangeAreaChart';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import { filterChartDataByName } from '@src/modules/performance/shared/helpers';
import ReportSection from './ReportSection';

const informationSource = {
  title: 'Smooth Travel Exposure',
  content: [
    {
      title: 'Ride quality (smooth travel on the roads)',
      description:
  <Text fontSize="inherit" mb={2}>The percentage of vehicle kilometres travelled (VKT) on the network each year that meets or exceeds smooth travel criteria ie. is on &apos;smooth&apos; sealed roads. This indicates the ride quality experienced by the road user. Higher results reflect an overall smoother ride for road users. </Text>,
      source:
  <Text fontSize="inherit" as="span">
    Sourced from
    {' '}
    <Link href="https://www.nzta.govt.nz/planning-and-investment/learning-and-resources/transport-data/data-and-tools/" isExternal>NZTA Data and Tools, road condition</Link>
  </Text>,
    },
  ],
};

function RoadConditionAmenitySealedRoads() {
  const rcaId = useRecoilValue(selectedDatabaseIdState);
  const currentDatabase = useRecoilValue(currentDatabaseSelector);
  const { data: rideQualityRoughness, isLoading } = useRideQualityRoughness();
  const isRegionNationalOrStateHighways = rcaId >= 1000 || rcaId === 81;
  const isNational = rcaId === 1000;
  let chartFilters = [];
  if (isNational) {
    chartFilters = ['Peer', 'Percentile'];
  } else if (isRegionNationalOrStateHighways) {
    chartFilters = ['Peer'];
  }

  // Ride Quality Roughness Chart
  const rideQualityRoughnessYears = [...new Set(rideQualityRoughness?.years?.flat())];
  const rideQualityRoughnessData = [{
    name: currentDatabase?.name,
    type: 'line',
    data: rideQualityRoughness?.values?.[0]?.map((value, index) => ({ x: rideQualityRoughnessYears[index], y: value })),
    color: CHART_COLOURS.blue,
  }, {
    name: 'Peer Group',
    type: 'line',
    data: rideQualityRoughness?.values?.[1]?.map((value, index) => ({ x: rideQualityRoughnessYears[index], y: value })),
    color: CHART_COLOURS.black,
  }, {
    name: '25-75th Percentile',
    type: 'rangeArea',
    data: rideQualityRoughnessYears.map((year, index) => ({ x: year, y: [rideQualityRoughness?.values?.[2]?.[index], rideQualityRoughness?.values?.[3]?.[index]] })),
    color: CHART_COLOURS.lightgrey,
  }];

  // Filter out the Peer Group and Percentile data if it's a national report, and Peer Group if it's a region or state highways report
  const rideQualityRoughnessChartSeries = filterChartDataByName(
    rideQualityRoughnessData,
    chartFilters,
  );

  const markers = isRegionNationalOrStateHighways ? {
    size: [3, 0],
    hover: {
      size: 6,
    },
  } : {
    size: [3, 3, 0],
    hover: {
      size: 6,
    },
  };

  const stroke = isRegionNationalOrStateHighways ? {
    curve: 'straight',
    width: [2, 0],
  } : {
    curve: 'straight',
    width: [2, 2, 0],
  };

  return (
    <ReportSection
      title="Smooth Travel Exposure"
      isLoading={isLoading || !rideQualityRoughness}
      lockYear={rideQualityRoughness?.lockYear}
      informationSource={informationSource}
    >
      <LineRangeAreaChart
        title="Ride quality (smooth travel on the roads)"
        categories={rideQualityRoughnessYears}
        series={rideQualityRoughnessChartSeries}
        width="100%"
        height="280"
        markers={markers}
        stroke={stroke}
        prefix=""
        suffix="%"
        decimalPoints={1}
        yAxisDecimalPoints={1}
      />
      <GroupedRegionMessage rcaIds={[1014, 1015, 1016]} />
    </ReportSection>
  );
}

export default RoadConditionAmenitySealedRoads;
